import type { MeResponse } from '@signapass/shared/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, use, type ReactNode } from 'react';
import { ApiError, api } from './api.js';

type Principal = MeResponse['principal'];

interface AuthState {
  principal: Principal | null;
  isLoading: boolean;
  /** Drops cached data so a second sign-in cannot see the first one's rows. */
  reset: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Resolves the current principal from the session cookie.
 *
 * There is no token in memory and none in storage. `GET /auth/me` is the only
 * way the SPA learns who it is talking for, which means a stale tab discovers
 * its session has ended the next time it asks rather than acting on a token it
 * still holds locally.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api<MeResponse>('/auth/me'),
    retry: (failureCount, error) => {
      // A 401 means signed out, not a transient failure. Retrying it delays
      // the redirect to the login screen for no benefit.
      if (error instanceof ApiError && error.status === 401) return false;
      return failureCount < 2;
    },
    staleTime: 60_000,
  });

  const value: AuthState = {
    principal: data?.principal ?? null,
    isLoading,
    reset: async () => {
      // Removed, not invalidated. Invalidating leaves the previous account's
      // data on screen while the refetch is in flight.
      queryClient.clear();
    },
  };

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthState {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}

/**
 * The platform admin's own session, for `RequirePlatformAdmin`. Shared so the
 * platform login can drop a cached "signed out" before navigating in.
 */
export const PLATFORM_ME_KEY = ['platform', 'me'] as const;

type UserPrincipal = Extract<Principal, { type: 'USER' }>;

/** Whether this principal may see the merchant dashboard. */
export function isDashboardPrincipal(principal: Principal | null): boolean {
  return principal?.type === 'USER' || principal?.type === 'PLATFORM_ADMIN';
}

/**
 * Narrows to a merchant-side principal, for dashboard-only code paths.
 *
 * Support inside a merchant's account (features/PLATFORM-ADMIN.md) is shown as the
 * owner. The server passes an impersonating admin through every role check
 * (`RolesGuard`), so the owner's screens are the ones that work for them; a
 * manager's narrower view would hide pages support can open and is often
 * asked about.
 */
export function useUser(): UserPrincipal | null {
  const { principal } = useAuth();
  if (principal?.type === 'USER') return principal;
  if (principal?.type === 'PLATFORM_ADMIN') {
    return {
      type: 'USER',
      id: principal.id,
      email: '',
      fullName: 'SignaPass support',
      role: 'OWNER',
      organizationId: principal.organizationId,
      locationId: null,
      brandId: null,
    };
  }
  return null;
}

export function useStaff() {
  const { principal } = useAuth();
  return principal?.type === 'STAFF' ? principal : null;
}

type CustomerPrincipal = Extract<Principal, { type: 'CUSTOMER' }>;

/**
 * The customer session, asked for by its own route — LOY-8.
 *
 * Not read from `useAuth`: `/auth/me` answers as the merchant whenever this
 * browser is also signed in to the dashboard, and the customer card would then
 * see no customer and bounce back to its login. Keyed under `['auth', 'me']`
 * so the sign-in's invalidation and sign-out's `clear()` reach it too.
 */
export function useCustomerSession(): { customer: CustomerPrincipal | null; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'me', 'customer'],
    queryFn: async () => {
      try {
        return await api<MeResponse>('/auth/customer/me');
      } catch (error) {
        // Signed out is an answer, not a failure to retry.
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: 2,
    staleTime: 60_000,
  });
  const principal = data?.principal;
  return { customer: principal?.type === 'CUSTOMER' ? principal : null, isLoading };
}

export function useCustomer() {
  return useCustomerSession().customer;
}
